import { useEffect, useMemo, useRef } from 'react'
import { $getSelection, $isRangeSelection, type EditorState } from 'lexical'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  type LayoutNode,
  getEffectiveTextStyle,
  runsToString,
} from '@backend/lib/db/typeRoots/layoutNode'
import { breakLines, calculateTextHeight } from '../drawCanvas'
import { nodeStore, useNodeStore } from '@/stores/nodeStore'
import { rewrapFromContent } from '@/lib/rewrapTextNodesFromRuns'
import { colorToString } from '../color'
import { RichTextNode } from './RichTextNode'
import {
  populateEditorFromRuns,
  readRunsFromEditor,
  readRunsFromEditorState,
  runsEqual,
} from './runBridge'
import { applyStyleToSelection, readSelectionStyle } from './applyStyle'
import { useEditorContextSetter } from './EditorContext'
import { NormalizePlainTextPlugin } from './NormalizePlainTextPlugin'

type TextNodeData = Extract<LayoutNode['data'], { type: 'text' }>

type Props = {
  node: LayoutNode & { data: TextNodeData }
  nodeIndex: number
  panX: number
  panY: number
  zoom: number
  ctx: CanvasRenderingContext2D
  onCommit: () => void
}

const onError = (error: Error) => {
  console.error('Lexical editor error:', error)
}

/** Replaces the legacy <textarea> overlay with a Lexical editor that
 *  preserves the multi-run RichTextRun[] content while the user types.
 *  The wrapper div is positioned/sized to match what drawCanvas would
 *  paint — so toggling edit mode is visually seamless. */
export const CanvasTextEditor = (props: Props) => {
  const initialRuns = props.node.data.content
  const initialConfig = useMemo(
    () => ({
      namespace: 'CanvasTextEditor',
      nodes: [RichTextNode],
      onError,
      editorState: () => populateEditorFromRuns(initialRuns),
      theme: {},
    }),
    // The initial state is fixed at mount. Subsequent runs flow through
    // Lexical itself; switching edited node remounts via React `key`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <PositionedSurface {...props} />
    </LexicalComposer>
  )
}

const PositionedSurface = ({ node, nodeIndex, panX, panY, zoom, ctx, onCommit }: Props) => {
  const [editor] = useLexicalComposerContext()
  const wrapperRef = useRef<HTMLDivElement>(null)
  const { updateNode } = useNodeStore()
  const fallbackStyleRef = useRef(getEffectiveTextStyle(node.data))
  useEffect(() => {
    fallbackStyleRef.current = getEffectiveTextStyle(node.data)
  }, [node.data])

  useEffect(() => {
    const wrapper = wrapperRef.current
    if (!wrapper) return

    const effective = getEffectiveTextStyle(node.data)
    const opts = node.data.options
    const padTop = opts.paddingTop ?? 0
    const padRight = opts.paddingRight ?? 0
    const padBottom = opts.paddingBottom ?? 0
    const padLeft = opts.paddingLeft ?? 0
    const innerX = node.x + padLeft
    const innerY = node.y + padTop
    const innerW = Math.max(0, node.width - padLeft - padRight)
    const innerH = Math.max(0, node.height - padTop - padBottom)

    const lines = breakLines(
      runsToString(node.data.content),
      innerW,
      ctx,
      effective.fontSize,
      effective.fontFamily,
      effective.fontWeight,
      effective.fontStyle,
    )
    const textHeight = calculateTextHeight(lines, effective.fontSize, opts.lineHeight)

    const top =
      opts.verticalAlign === 'center'
        ? innerY + innerH / 2 - textHeight / 2
        : opts.verticalAlign === 'bottom'
          ? innerY + innerH - textHeight
          : innerY

    wrapper.style.position = 'absolute'
    wrapper.style.left = `${panX + innerX * zoom}px`
    wrapper.style.top = `${panY + top * zoom}px`
    wrapper.style.width = `${innerW}px`
    wrapper.style.minHeight = `${textHeight}px`
    wrapper.style.lineHeight = `${opts.lineHeight}`
    wrapper.style.textAlign = opts.horizontalAlign
    wrapper.style.transform = `scale(${zoom})`
    wrapper.style.transformOrigin = '0 0'
    wrapper.style.color = colorToString(effective.fontColor)
    wrapper.style.background = 'transparent'
    wrapper.style.marginLeft = '2px'
    wrapper.style.marginTop = '-2px'
    wrapper.style.overflow = 'visible'
    wrapper.style.whiteSpace = 'pre-wrap'
    wrapper.style.wordWrap = 'break-word'
  }, [node, ctx, panX, panY, zoom])

  useEffect(() => {
    editor.focus()
  }, [editor])

  // Publish the editor handle and selection state into the EditorContext so
  // the workspace toolbar can route style updates to the active selection.
  // `lastRange` is sticky — we keep it cached after the contenteditable
  // blurs (e.g. user clicks a Radix popover that portals out of the
  // toolbar wrapper) so the toolbar can still target the original span.
  const setContext = useEditorContextSetter()
  useEffect(() => {
    setContext({ editor, selectionStyle: null, hasRangeSelection: false, lastRange: null })
    const unregister = editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection()
        if (!$isRangeSelection(selection)) {
          setContext({ selectionStyle: null, hasRangeSelection: false })
          return
        }
        const hasRange = !selection.isCollapsed()
        const style = readSelectionStyle(selection)
        setContext({
          selectionStyle: hasRange ? style : null,
          hasRangeSelection: hasRange,
          ...(hasRange
            ? {
                lastRange: {
                  anchorKey: selection.anchor.key,
                  anchorOffset: selection.anchor.offset,
                  focusKey: selection.focus.key,
                  focusOffset: selection.focus.offset,
                },
              }
            : {}),
        })
      })
    })
    return () => {
      unregister()
      setContext({ editor: null, selectionStyle: null, hasRangeSelection: false, lastRange: null })
    }
  }, [editor, setContext])

  const handleChange = (editorState: EditorState) => {
    const newRuns = readRunsFromEditorState(editorState, fallbackStyleRef.current)
    if (runsEqual(newRuns, node.data.content)) return
    // Empty-content equivalence: a node with content=[] populates as an
    // empty paragraph, which read-back synthesizes as `[{text:'', ...}]`.
    // Without this short-circuit, every enter-edit on an empty node fires
    // a spurious store update on mount.
    const oldText = node.data.content.map((r) => r.text).join('')
    const newText = newRuns.map((r) => r.text).join('')
    if (oldText === '' && newText === '') return
    // Content changed — invalidate the cached lastRange. Its anchor/focus
    // keys may now point to nodes that were split, removed, or re-keyed by
    // the just-applied edit; reusing it from the toolbar would target stale
    // offsets. Live `hasRangeSelection` still works for in-edit selections.
    setContext({ lastRange: null })
    updateNode(nodeIndex, { data: { content: newRuns } })
  }

  const commitAndExit = () => {
    // Read straight from the live editor state so the commit isn't racing
    // the last OnChange-driven store update.
    const latestRuns = readRunsFromEditor(editor, fallbackStyleRef.current)

    // Read the latest node from the store, not from the React closure —
    // OnChange may have just written content that React hasn't re-rendered
    // through yet. Without this, the closure's stale `node.data.content`
    // makes runsEqual return false and we rewrap unnecessarily on every
    // type-then-Esc.
    const liveStoreNode = nodeStore.getState().nodes[nodeIndex]
    const liveContent =
      liveStoreNode?.data.type === 'text' ? liveStoreNode.data.content : node.data.content

    // If the runs round-trip identically — user opened edit mode and exited
    // without typing anything — keep the captured layout untouched. The
    // rewrap is only needed when content actually changed; running it on
    // an unchanged node introduces measure-text drift that can shift line
    // breaks (eval edit-cycle test caught this).
    if (runsEqual(latestRuns, liveContent)) {
      updateNode(nodeIndex, { data: { editMode: false } }, { history: true })
      onCommit()
      return
    }

    const liveNode = { ...node, data: { ...node.data, content: latestRuns } }
    const fresh = rewrapFromContent(liveNode, ctx)
    updateNode(
      nodeIndex,
      {
        data: {
          content: latestRuns,
          editMode: false,
          options: { preWrappedLines: fresh ?? undefined },
        },
      },
      { history: true },
    )
    onCommit()
  }

  // Decide commit-vs-stay-in-edit from the actual click target, not from
  // the contenteditable's blur event. Radix popovers (color picker etc.)
  // portal their content out of the toolbar wrapper, so they trigger blur
  // even though the user is still working inside an editor-related UI.
  // Walking up from the pointerdown target lets us see all of those cases.
  const commitRef = useRef(commitAndExit)
  useEffect(() => {
    commitRef.current = commitAndExit
  })
  useEffect(() => {
    const isInsideEditingUi = (el: HTMLElement | null) => {
      if (!el) return false
      if (wrapperRef.current?.contains(el)) return true
      if (el.closest('[data-canvas-text-toolbar]')) return true
      // Radix popovers and dropdowns portal to body — match their wrappers.
      if (el.closest('[data-radix-popper-content-wrapper]')) return true
      // Other Radix surfaces and ambient overlays we shouldn't commit on:
      // dialogs, tooltips, hover cards, toasts. The user is interacting
      // with chrome that doesn't represent "leaving the editor."
      if (el.closest('[role="dialog"]')) return true
      if (el.closest('[role="tooltip"]')) return true
      if (el.closest('[role="status"]')) return true
      if (el.closest('[data-radix-tooltip-content]')) return true
      if (el.closest('[data-radix-hover-card-content]')) return true
      if (el.closest('[data-sonner-toast]')) return true
      if (el.closest('[data-sonner-toaster]')) return true
      return false
    }
    const handlePointerDown = (e: PointerEvent) => {
      if (isInsideEditingUi(e.target as HTMLElement | null)) return
      commitRef.current()
    }
    document.addEventListener('pointerdown', handlePointerDown, true)
    return () => document.removeEventListener('pointerdown', handlePointerDown, true)
  }, [])

  return (
    <div ref={wrapperRef}>
      <PlainTextPlugin
        contentEditable={
          <ContentEditable
            onKeyDown={(e) => {
              if (e.key === 'Delete' || e.key === 'Backspace') e.stopPropagation()
              // Esc commits and exits — keyboard counterpart to clicking
              // outside the editor's UI surface.
              if (e.key === 'Escape') {
                e.preventDefault()
                commitRef.current()
                return
              }

              // Cmd/Ctrl+B and Cmd/Ctrl+I toggle bold/italic on the active
              // selection. We don't use Lexical's FORMAT_TEXT_COMMAND because
              // our nodes carry explicit fontWeight/fontStyle fields rather
              // than the bitmask format used by the base TextNode. Read the
              // toggle decision from the live editor state — selectionStyleRef
              // can lag the actual selection by one update tick.
              const mod = e.metaKey || e.ctrlKey
              if (!mod) return
              const key = e.key.toLowerCase()
              if (key === 'b' || key === 'i') {
                e.preventDefault()
                const live = editor.getEditorState().read(() => {
                  const sel = $getSelection()
                  if (!$isRangeSelection(sel) || sel.isCollapsed()) return null
                  return readSelectionStyle(sel)
                })
                if (key === 'b') {
                  const isBold = (live?.fontWeight ?? 400) >= 700
                  applyStyleToSelection(editor, { fontWeight: isBold ? 400 : 700 })
                } else {
                  const isItalic = live?.fontStyle === 'italic'
                  applyStyleToSelection(editor, {
                    fontStyle: isItalic ? 'normal' : 'italic',
                  })
                }
              }
            }}
            style={{
              outline: 'none',
              border: 'none',
              background: 'transparent',
              minHeight: '1em',
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
            }}
          />
        }
        ErrorBoundary={LexicalErrorBoundary}
      />
      <NormalizePlainTextPlugin fallbackStyleRef={fallbackStyleRef} />
      <OnChangePlugin onChange={handleChange} />
    </div>
  )
}
